import React, { useEffect, useState } from 'react';
import './NotifyMeModal.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/;

const formatReleaseDate = (raw) => {
  if (!raw) return '';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch (e) {
    return '';
  }
};

const NotifyMeModal = ({
  isOpen,
  onClose,
  movie,
  prefill = {},
  onSubscribed,
  onUnsubscribed,
  api,
}) => {
  const [email, setEmail] = useState(prefill.email || '');
  const [mobile, setMobile] = useState(prefill.mobile || '');
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null); // { alreadySubscribed }
  const [initialStatus, setInitialStatus] = useState(null); // null | boolean

  // Pre-fill when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmail(prefill.email || '');
      setMobile(prefill.mobile || '');
      setEmailEnabled(true);
      setSmsEnabled(false);
      setWhatsappEnabled(false);
      setError('');
      setSuccess(null);

      // Check if the current visitor is already subscribed
      const checkStatus = async () => {
        if (!api || !movie?.id) return;
        try {
          const status = await api.getMovieNotificationStatus(movie.id, {
            email: prefill.email,
            mobile: prefill.mobile,
            userId: prefill.userId,
          });
          setInitialStatus(!!status.subscribed);
        } catch (e) {
          setInitialStatus(false);
        }
      };
      checkStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, movie?.id]);

  if (!isOpen) return null;

  const releaseStr = formatReleaseDate(movie?.releaseDate || movie?.releasedate);

  const validate = () => {
    const channels = [];
    if (emailEnabled) channels.push('email');
    if (smsEnabled) channels.push('sms');
    if (whatsappEnabled) channels.push('whatsapp');
    if (channels.length === 0) {
      return 'Please select at least one notification channel';
    }
    const hasEmail = email.trim().length > 0;
    const hasMobile = mobile.replace(/\s+/g, '').length > 0;
    if (!hasEmail && !hasMobile) {
      return 'Please enter your email or mobile number';
    }
    if (emailEnabled && hasEmail && !EMAIL_REGEX.test(email.trim())) {
      return 'Please enter a valid email address';
    }
    if ((smsEnabled || whatsappEnabled) && hasMobile) {
      const cleaned = mobile.replace(/\s+/g, '').replace(/^\+?91/, '');
      if (!MOBILE_REGEX.test(cleaned)) {
        return 'Please enter a valid 10-digit mobile number';
      }
    }
    if (smsEnabled && !hasMobile) {
      return 'Please enter a mobile number to receive SMS';
    }
    if (whatsappEnabled && !hasMobile) {
      return 'Please enter a mobile number to receive WhatsApp';
    }
    return null;
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const channels = [];
    if (emailEnabled) channels.push('email');
    if (smsEnabled) channels.push('sms');
    if (whatsappEnabled) channels.push('whatsapp');

    const cleanedMobile = mobile.replace(/\s+/g, '').replace(/^\+?91/, '');

    try {
      setSubmitting(true);
      const result = await api.subscribeToMovieNotifications(movie.id, {
        email: email.trim() || null,
        mobile: cleanedMobile || null,
        channels,
        userId: prefill.userId || null,
      });
      setSuccess({ alreadySubscribed: !!result.alreadySubscribed });
      setInitialStatus(true);
      if (onSubscribed) onSubscribed(result);
    } catch (err) {
      setError(
        err?.response?.error ||
          err?.message ||
          'Could not subscribe right now. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnsubscribe = async () => {
    setError('');
    try {
      setSubmitting(true);
      await api.unsubscribeFromMovieNotifications(movie.id, {
        email: email.trim() || prefill.email,
        mobile: mobile.replace(/\s+/g, '') || prefill.mobile,
        userId: prefill.userId || null,
      });
      setInitialStatus(false);
      setSuccess(null);
      if (onUnsubscribed) onUnsubscribed();
    } catch (err) {
      setError(
        err?.response?.error ||
          err?.message ||
          'Could not unsubscribe right now. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="notify-me-overlay" onClick={handleOverlayClick}>
      <div className="notify-me-modal" role="dialog" aria-modal="true">
        <button
          type="button"
          className="notify-me-close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>

        {success ? (
          <div className="notify-me-success">
            <div className="notify-me-success-icon">✓</div>
            <h3>
              {success.alreadySubscribed
                ? 'You are already subscribed'
                : 'You’re all set!'}
            </h3>
            <p>
              We’ll email you at <strong>{email}</strong>
              {mobile ? <> and message <strong>+91 {mobile.replace(/^\+?91/, '')}</strong></> : null}{' '}
              when:
            </p>
            <ul className="notify-me-success-events">
              <li>Tickets for <strong>{movie?.title}</strong> go on sale</li>
              <li>The movie releases {releaseStr && <>on <strong>{releaseStr}</strong></>}</li>
            </ul>
            <button
              type="button"
              className="notify-me-btn notify-me-btn-secondary"
              onClick={onClose}
            >
              Done
            </button>
          </div>
        ) : (
          <form className="notify-me-form" onSubmit={handleSubscribe}>
            <h3 className="notify-me-title">Notify Me</h3>
            <p className="notify-me-subtitle">
              Get notified about <strong>{movie?.title}</strong>
              {releaseStr && (
                <> — releases <strong>{releaseStr}</strong></>
              )}
              .
            </p>

            <label className="notify-me-label">Email</label>
            <input
              type="email"
              className="notify-me-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={!emailEnabled}
            />

            <label className="notify-me-label">Mobile (optional)</label>
            <input
              type="tel"
              className="notify-me-input"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="10-digit mobile number"
              autoComplete="tel"
              maxLength={14}
            />

            <fieldset className="notify-me-channels">
              <legend className="notify-me-label">Notify me via</legend>
              <label className="notify-me-channel">
                <input
                  type="checkbox"
                  checked={emailEnabled}
                  onChange={(e) => setEmailEnabled(e.target.checked)}
                />
                <span>Email</span>
              </label>
              <label className="notify-me-channel">
                <input
                  type="checkbox"
                  checked={smsEnabled}
                  onChange={(e) => setSmsEnabled(e.target.checked)}
                />
                <span>SMS</span>
              </label>
              <label className="notify-me-channel">
                <input
                  type="checkbox"
                  checked={whatsappEnabled}
                  onChange={(e) => setWhatsappEnabled(e.target.checked)}
                />
                <span>WhatsApp</span>
              </label>
            </fieldset>

            <p className="notify-me-fineprint">
              By subscribing, you agree to receive notifications about this
              movie. This is an informational alert, not a marketing message.
            </p>

            {error && <div className="notify-me-error">{error}</div>}

            {initialStatus && (
              <div className="notify-me-already">
                You’re already subscribed. Update your preferences or
                unsubscribe below.
                <button
                  type="button"
                  className="notify-me-link"
                  onClick={handleUnsubscribe}
                  disabled={submitting}
                >
                  Unsubscribe
                </button>
              </div>
            )}

            <div className="notify-me-actions">
              <button
                type="button"
                className="notify-me-btn notify-me-btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="notify-me-btn notify-me-btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Subscribing…' : 'Notify Me'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default NotifyMeModal;